import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  X,
  Plus,
  Users,
  AtSign
} from 'lucide-react';

interface EmailRecipientsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSendEmail: (recipients: string[]) => void;
  obraData?: {
    responsaveis_obra?: string[];
    clientes_responsaveis?: string[];
  };
}

export const EmailRecipientsModal: React.FC<EmailRecipientsModalProps> = ({
  open,
  onOpenChange,
  onSendEmail,
  obraData
}) => {
  const [selectedInternalEmails, setSelectedInternalEmails] = useState<string[]>([]);
  const [externalEmails, setExternalEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');

  // Combine all internal emails from obra data
  const internalEmails = [
    ...(obraData?.responsaveis_obra || []),
    ...(obraData?.clientes_responsaveis || [])
  ].filter(email => email && email.includes('@'));

  const handleInternalEmailToggle = (email: string) => {
    setSelectedInternalEmails(prev => 
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleAddExternalEmail = () => {
    const trimmedEmail = newEmail.trim();
    if (trimmedEmail && trimmedEmail.includes('@') && !externalEmails.includes(trimmedEmail)) {
      setExternalEmails(prev => [...prev, trimmedEmail]);
      setNewEmail('');
    }
  };

  const handleRemoveExternalEmail = (email: string) => {
    setExternalEmails(prev => prev.filter(e => e !== email));
  };

  const handleSendEmail = () => {
    const allRecipients = [...selectedInternalEmails, ...externalEmails];
    if (allRecipients.length > 0) {
      onSendEmail(allRecipients);
      onOpenChange(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddExternalEmail();
    }
  };

  const totalRecipients = selectedInternalEmails.length + externalEmails.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Selecionar destinatários
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Escolha quem receberá o relatório por email.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Internal emails from obra */}
          {internalEmails.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <Label className="font-medium">Responsáveis da obra</Label>
              </div>
              <div className="space-y-2 pl-6">
                {internalEmails.map((email) => (
                  <div
                    key={email}
                    className="flex items-center space-x-3 p-2 rounded-lg border hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      id={`internal-${email}`}
                      checked={selectedInternalEmails.includes(email)}
                      onCheckedChange={() => handleInternalEmailToggle(email)}
                    />
                    <Label
                      htmlFor={`internal-${email}`}
                      className="flex-1 cursor-pointer text-sm"
                    >
                      {email}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {internalEmails.length > 0 && <Separator />}

          {/* External emails */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AtSign className="w-4 h-4" />
              <Label className="font-medium">Emails externos</Label>
            </div>
            
            <div className="flex gap-2">
              <Input
                placeholder="Digite um email externo..."
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleAddExternalEmail}
                disabled={!newEmail.trim() || !newEmail.includes('@')}
                size="sm"
                variant="outline"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {externalEmails.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Emails adicionados:
                </Label>
                <div className="flex flex-wrap gap-2">
                  {externalEmails.map((email) => (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {email}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={() => handleRemoveExternalEmail(email)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {totalRecipients === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700">
                Selecione pelo menos um destinatário para enviar o email.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSendEmail}
            disabled={totalRecipients === 0}
            className="min-w-[120px]"
          >
            <Mail className="w-4 h-4 mr-2" />
            Enviar ({totalRecipients})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};